// Shared formatters. The port of the old `util.js`, minus `esc` and `$`.
//
// `esc` is gone because JSX escapes interpolated values by default -- which is the single
// biggest reason for this migration. It was called by hand at roughly six hundred
// interpolation sites, and one miss was stored XSS behind a sign-in. There is no
// `dangerouslySetInnerHTML` anywhere in this app, and there should not be.
//
// `$` is gone because nothing reaches into the document any more.

export const SALESFORCE_ACCOUNT_URL =
  'https://cartesia.lightning.force.com/lightning/r/Account/';

export function formatCount(value: number | null | undefined): string {
  return (value ?? 0).toLocaleString();
}

/** Headcount, compact: 1,250 below 10k, then 12k, then 1.2M. A dash when unknown. */
export function formatHeadcount(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  if (value < 10_000) return value.toLocaleString();
  const thousands = Math.round(value / 1000);
  if (thousands < 1000) return `${thousands}k`;
  const millions = Math.round(value / 100_000) / 10;
  return `${millions}M`;
}

/** "Omair Khan" → "Omair": the first word of a full name. */
export function firstName(fullName: string): string {
  return fullName.trim().split(/\s+/)[0] ?? '';
}

export function formatMoney(value: number | null | undefined): string | null {
  return value == null ? null : '$' + Math.round(value).toLocaleString();
}

export function formatCredits(value: number | null | undefined): string {
  return value == null ? '—' : Math.round(value).toLocaleString();
}

// Whole days between the mirror's "today" and a date string. `today` is the payload's own
// date rather than the browser's: the page is only ever as current as the sync behind it, and
// a laptop in another timezone must not shift what counts as stale.
export function daysSince(day: string, today: Date): number {
  return Math.round((today.getTime() - new Date(day + 'T00:00:00Z').getTime()) / 864e5);
}

const PACIFIC = 'America/Los_Angeles';

// The Pacific calendar day a stamp belongs to. A bare YYYY-MM-DD is already that day — the
// board's usual shape — and must not be parsed as UTC midnight, which is the previous evening
// in Pacific and is how 23% of rows land a day out.
export function pacificCalendarDay(value: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  const formatted = new Intl.DateTimeFormat('en-CA', {
    timeZone: PACIFIC,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(parsed);
  return formatted;
}

// The real Pacific calendar day, not the book's `today`. Date inputs that cap at the
// payload's day lock the current date out between midnight and the next Fivetran sync.
export function pacificToday(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: PACIFIC,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

// Chip-sized date. Last out prints the ISO day in mono; chips sit among labels like
// "Not yet contacted" and have to be readable at a glance.
export function formatShortDay(value: string): string {
  const day = pacificCalendarDay(value);
  const parsed = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return day;
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(parsed);
}

// A company's own site, preferred over Salesforce. `domain` is populated on 313 of the 314
// accounts and `website` on 261, so the domain is what this is built from; the one account
// with neither gets no link at all rather than a dead one.
export function companyUrl(
  domain: string | null,
  website: string | null,
): string | null {
  const source = domain || (website || '').replace(/^https?:\/\//i, '').replace(/\/.*$/, '');
  if (!source) return null;
  return httpsUrl(source);
}

// Mirror-sourced URLs arrive with a mix of schemes and sometimes none at all.
export function httpsUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  return trimmed
    .replace(/^http:\/\//i, 'https://')
    .replace(/^(?!https?:\/\/)/i, 'https://');
}

// Machine tokens throughout the Clay blocks: enums arrive snake_cased and booleans arrive
// Python-cased, so `True` and `voice_shipped` both need a pass before they are readable.
export function humanValue(value: unknown): string | null {
  if (value == null || value === '') return null;
  const text = String(value).trim();
  if (/^(true|false)$/i.test(text)) return text.toLowerCase() === 'true' ? 'Yes' : 'No';
  return text.replace(/_/g, ' ');
}

export function titleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

// The panel prints `reason · change`. Still-open rows used to write the same
// clause into both fields, so each line appeared twice. Skip a duplicate.
export function briefLineWhy(line: { reason: string; change?: string }): string {
  if (!line.change || line.change === line.reason) return line.reason;
  return line.reason + ' · ' + line.change;
}

// A trailing legal suffix and the punctuation before it: ", Inc.", " Private Limited", " GmbH".
const LEGAL_SUFFIX = new RegExp(
  '[\\s,]+(?:private\\s+limited|pvt\\.?\\s+ltd\\.?|pty\\.?\\s+ltd\\.?|inc\\.?|l\\.l\\.c\\.|llc|ltd\\.?|limited|gmbh|s\\.a\\.|corporation|corp\\.?|co\\.?|plc|ag|bv)$',
  'i',
);
const TRAILING_TAG = /\s*\([^)]*\)\s*$/;

/**
 * The account name as a table cell shows it: the legal suffix dropped ("Deem, Inc." → "Deem"),
 * a trailing "(YC W25)" tag kept. The full name stays in the hover card, copies and exports.
 * Never returns an empty name: a name that is only a suffix comes back as it was.
 */
export function displayAccountName(name: string): string {
  const tag = name.match(TRAILING_TAG)?.[0] ?? '';
  let head = tag ? name.slice(0, name.length - tag.length) : name;
  head = head.trim();

  // A name can carry more than one suffix ("Foo Pvt. Ltd."), so strip until none is left.
  let stripped = head;
  while (LEGAL_SUFFIX.test(stripped)) stripped = stripped.replace(LEGAL_SUFFIX, '').trim();

  // "Smith & Co" is not "Smith &"; and a name that is only a suffix stays whole.
  if (!stripped || /(?:&|\band)$/i.test(stripped)) return name;
  return tag ? `${stripped} ${tag.trim()}` : stripped;
}
